import { useEffect, useMemo, useState } from "react";
import { BookOpen, ChevronDown, ChevronRight, FileText, Layers, Library, Loader2, Trash2 } from "lucide-react";
import { Dialog } from "@/components/modals";
import { ApiError, errorMessage, post } from "@/lib/api";
import {
  type CourseBreakerMark,
  type CourseBreakerNode,
  type CourseBreakerTree,
} from "@/lib/courseBreaker";

type Preview = {
  deleteCourse: boolean;
  willDelete: {
    course: boolean;
    containers: number;
    activities: number;
    questions: number;
    questionsOnly: number;
  };
  missingQuestions: Array<{ activityId: string; title: string; questionCount: number }>;
  learner: {
    progress: number;
    submissions: number;
    responses: number;
    discussions: number;
    threads: number;
    total: number;
  };
  enrollments: number;
  enrollmentsAssigned: number;
  pendingProposals: number;
  announcements: number;
  copies: { courses: number; containers: number; activities: number; questions: number };
  libraryAttaches: number;
  blockers: Array<{ code: string; message: string }>;
  canApply: boolean;
  nothing: boolean;
};

function outlineKey(kind: string, id: string) {
  return `${kind}:${id}`;
}

function collectActivityIds(node: CourseBreakerNode, into: string[] = []) {
  if (node.kind === "item") into.push(node.id);
  for (const child of node.children) collectActivityIds(child, into);
  return into;
}

function descendantKeys(node: CourseBreakerNode, into: Set<string> = new Set()) {
  for (const child of node.children) {
    into.add(outlineKey(child.kind, child.id));
    descendantKeys(child, into);
  }
  return into;
}

function expandableIds(nodes: CourseBreakerNode[]) {
  const ids: string[] = [];
  const walk = (node: CourseBreakerNode) => {
    if (node.children.length) ids.push(node.id);
    for (const child of node.children) walk(child);
  };
  for (const node of nodes) walk(node);
  return ids;
}

function Mark({ mark }: { mark: CourseBreakerMark }) {
  return (
    <span className={`al-cb-deck-mark al-cb-deck-mark--${mark}`} aria-hidden="true">
      {mark}
    </span>
  );
}

function NodeIcon({ mark }: { mark: CourseBreakerMark }) {
  const props = { size: 11, "aria-hidden": true as const };
  if (mark === "C") return <Library {...props} />;
  if (mark === "U") return <Layers {...props} />;
  if (mark === "L") return <BookOpen {...props} />;
  return <FileText {...props} />;
}

function countLabel(n: number, one: string, many: string) {
  return `${n} ${n === 1 ? one : many}`;
}

export default function CourseBreakerDialog({
  open,
  tree,
  canDeleteCourse = false,
  onClose,
  onRemoved,
}: {
  open: boolean;
  tree: CourseBreakerTree | null;
  canDeleteCourse?: boolean;
  onClose: () => void;
  onRemoved: (removedCourse: boolean) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [includeQuestions, setIncludeQuestions] = useState<Set<string>>(() => new Set());
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [includeProgress, setIncludeProgress] = useState(false);
  const [includeEnrollments, setIncludeEnrollments] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const courseNode = tree?.roots[0] || null;
  const implied = useMemo(() => {
    const next = new Set<string>();
    if (!courseNode) return next;
    const walk = (node: CourseBreakerNode, ancestorOn: boolean) => {
      const key = outlineKey(node.kind, node.id);
      const on = ancestorOn || selected.has(key);
      if (on) next.add(key);
      for (const child of node.children) walk(child, on);
    };
    walk(courseNode, false);
    return next;
  }, [courseNode, selected]);

  const deleteCourse = Boolean(courseNode && selected.has(outlineKey("course", courseNode.id)));

  const body = useMemo(() => {
    if (!tree || !courseNode) {
      return {
        deleteCourse: false,
        containerIds: [] as string[],
        activityIds: [] as string[],
        includeQuestionActivityIds: [] as string[],
        includeProgress: false,
        includeEnrollments: false,
      };
    }
    const containerIds: string[] = [];
    const activityIds: string[] = [];
    const walk = (node: CourseBreakerNode, ancestorOn: boolean) => {
      const key = outlineKey(node.kind, node.id);
      const on = selected.has(key);
      if (!ancestorOn && on) {
        if (node.kind === "container") containerIds.push(node.id);
        if (node.kind === "item") activityIds.push(node.id);
      }
      for (const child of node.children) walk(child, ancestorOn || on);
    };
    walk(courseNode, false);
    return {
      deleteCourse,
      containerIds,
      activityIds,
      includeQuestionActivityIds: [...includeQuestions],
      includeProgress,
      includeEnrollments,
    };
  }, [tree, courseNode, selected, includeQuestions, includeProgress, includeEnrollments, deleteCourse]);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setIncludeQuestions(new Set());
    setIncludeProgress(false);
    setIncludeEnrollments(false);
    setUnderstood(false);
    setPreview(null);
    setError(null);
    setExpanded(new Set(tree ? expandableIds(tree.roots) : []));
  }, [open, tree?.courseId]);

  useEffect(() => {
    if (!open || !tree) return undefined;
    let cancelled = false;
    setPreviewing(true);
    const handle = window.setTimeout(() => {
      void (async () => {
        try {
          const env = await post<{ success: boolean; data: Preview }>(
            `/api/courses/${encodeURIComponent(tree.courseId)}/breaker/preview`,
            body,
          );
          if (cancelled) return;
          setPreview(env.data || null);
          setError(null);
        } catch (err) {
          if (cancelled) return;
          setError(errorMessage(err));
        } finally {
          if (!cancelled) setPreviewing(false);
        }
      })();
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [open, tree, body]);

  const toggleNode = (node: CourseBreakerNode, ancestorOn: boolean) => {
    if (ancestorOn) return;
    if (node.kind === "course" && !canDeleteCourse) return;
    const key = outlineKey(node.kind, node.id);
    const activityIds = node.kind === "item" ? [node.id] : collectActivityIds(node);
    setSelected((prev) => {
      const next = new Set(prev);
      const turningOn = !next.has(key);
      if (turningOn) next.add(key);
      else {
        next.delete(key);
        for (const childKey of descendantKeys(node)) next.delete(childKey);
      }
      setIncludeQuestions((current) => {
        const merged = new Set(current);
        if (turningOn) {
          const scan = (row: CourseBreakerNode) => {
            if (row.kind === "item" && row.questionCount > 0 && activityIds.includes(row.id)) {
              merged.add(row.id);
            }
            for (const child of row.children) scan(child);
          };
          scan(node);
        } else {
          for (const id of activityIds) merged.delete(id);
        }
        return merged;
      });
      return next;
    });
  };

  const toggleQuestions = (activityId: string, on: boolean) => {
    setIncludeQuestions((prev) => {
      const next = new Set(prev);
      if (on) next.add(activityId);
      else next.delete(activityId);
      return next;
    });
  };

  const apply = async () => {
    if (!tree || !preview?.canApply || !understood) return;
    setBusy(true);
    setError(null);
    try {
      await post(`/api/courses/${encodeURIComponent(tree.courseId)}/breaker/apply`, {
        ...body,
        confirm: true,
      });
      onRemoved(deleteCourse);
    } catch (err) {
      if (err instanceof ApiError && err.payload && typeof err.payload === "object") {
        const payload = err.payload as { data?: Preview };
        if (payload.data) setPreview(payload.data);
      }
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const renderNode = (node: CourseBreakerNode, depth: number, ancestorOn: boolean) => {
    const key = outlineKey(node.kind, node.id);
    const on = ancestorOn || implied.has(key);
    const openRow = expanded.has(node.id);
    const nested = node.children.filter((child) => child.kind !== "item");
    const items = node.children.filter((child) => child.kind === "item");
    const hasKids = node.children.length > 0;
    return (
      <div key={key} className="al-cb-break-block">
        <div
          className={`al-cb-outline-row al-cb-outline-row--${node.mark}${on ? " al-cb-outline-row--on" : ""}${ancestorOn ? " al-cb-break-row--implied" : ""}`}
          style={{ ["--d" as string]: depth }}
        >
          {hasKids ? (
            <button
              type="button"
              className="al-cb-outline-caret"
              aria-label={openRow ? "Collapse" : "Expand"}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setExpanded((prev) => {
                  const next = new Set(prev);
                  if (next.has(node.id)) next.delete(node.id);
                  else next.add(node.id);
                  return next;
                });
              }}
            >
              {openRow ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
          ) : (
            <span className="al-cb-outline-caret-spacer" />
          )}
          <label className="al-cb-break-pick">
            <input
              type="checkbox"
              checked={on}
              disabled={ancestorOn || busy || (node.kind === "course" && !canDeleteCourse)}
              onChange={() => toggleNode(node, ancestorOn)}
            />
            <Mark mark={node.mark} />
            <NodeIcon mark={node.mark} />
            <span className="al-cb-outline-row-text">
              <span className="al-cb-outline-row-kicker">{node.label}</span>
              <span className="al-cb-outline-row-title">{node.title}</span>
            </span>
          </label>
        </div>
        {node.kind === "item" && node.questionCount > 0 ? (
          <label className="al-cb-break-questions" style={{ ["--d" as string]: depth + 1 }}>
            <input
              type="checkbox"
              checked={includeQuestions.has(node.id)}
              disabled={busy}
              onChange={(event) => toggleQuestions(node.id, event.target.checked)}
            />
            Include all questions ({node.questionCount})
          </label>
        ) : null}
        {openRow ? (
          <>
            {nested.map((child) => renderNode(child, depth + 1, on))}
            {items.map((child) => renderNode(child, depth + 1, on))}
          </>
        ) : items.length ? (
          <p className="al-cb-break-hidden" style={{ ["--d" as string]: depth + 1 }}>
            {countLabel(items.length, "item hidden", "items hidden")} — expand to choose questions
          </p>
        ) : null}
      </div>
    );
  };

  const copies =
    (preview?.copies.courses || 0) +
    (preview?.copies.containers || 0) +
    (preview?.copies.activities || 0) +
    (preview?.copies.questions || 0);
  const summaryBits = preview
    ? [
        preview.willDelete.course ? "this course" : null,
        preview.willDelete.containers
          ? countLabel(preview.willDelete.containers, "container", "containers")
          : null,
        preview.willDelete.activities
          ? countLabel(preview.willDelete.activities, "activity", "activities")
          : null,
        preview.willDelete.questions
          ? countLabel(preview.willDelete.questions, "question", "questions")
          : null,
      ].filter(Boolean)
    : [];

  return (
    <Dialog
      open={open}
      title="Course Breaker"
      subtitle="Permanent. Choose the outline rows to remove. Nested lessons and items go with a selected container."
      onClose={() => {
        if (!busy) onClose();
      }}
      width="min(42rem, 100%)"
      maxHeight="min(92dvh, 52rem)"
      escapeClose={!busy}
      footer={
        <div className="al-cb-break-foot">
          <button type="button" className="al-cb-btn al-cb-btn--ghost" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="al-cb-btn al-cb-btn--danger"
            disabled={busy || !understood || !preview?.canApply}
            onClick={() => void apply()}
          >
            {busy ? <Loader2 size={14} className="al-cb-add-spin" /> : <Trash2 size={14} />}
            {deleteCourse ? "Delete course" : "Delete selected"}
          </button>
        </div>
      }
    >
      {!tree ? (
        <p className="al-cb-muted">Course not found.</p>
      ) : (
        <div className="al-cb-break">
          <p className="al-cb-break-warn">
            This cannot be undone. Copies of this outline in other courses keep their content; they only lose the
            source link.
          </p>
          {!canDeleteCourse ? (
            <p className="al-cb-muted">
              Deleting the whole course needs Remove Courses (school copies and shelf drafts) or Remove Master
              Courses (catalog templates). Outline rows can still be removed.
            </p>
          ) : null}
          <div className="al-cb-break-tree" role="tree">
            {courseNode ? renderNode(courseNode, 0, false) : null}
          </div>

          <div className="al-cb-break-impact" aria-live="polite">
            <header>
              <h3>What this will remove</h3>
              {previewing ? <Loader2 size={14} className="al-cb-add-spin" /> : null}
            </header>
            {error ? <p className="al-cb-break-error">{error}</p> : null}
            {preview?.nothing ? (
              <p className="al-cb-muted">
                {canDeleteCourse
                  ? "Select a unit, lesson, activity, or the course."
                  : "Select a unit, lesson, or activity."}
              </p>
            ) : summaryBits.length ? (
              <p>Will delete {summaryBits.join(", ")}.</p>
            ) : null}

            {preview && preview.learner.total > 0 && !deleteCourse ? (
              <label className="al-cb-check al-cb-break-opt">
                <input
                  type="checkbox"
                  checked={includeProgress}
                  disabled={busy}
                  onChange={(event) => setIncludeProgress(event.target.checked)}
                />
                Also delete learner work ({countLabel(preview.learner.progress, "progress row", "progress rows")},{" "}
                {countLabel(preview.learner.submissions, "submission", "submissions")}
                {preview.learner.responses
                  ? `, ${countLabel(preview.learner.responses, "response", "responses")}`
                  : ""}
                {preview.learner.discussions
                  ? `, ${countLabel(preview.learner.discussions, "discussion post", "discussion posts")}`
                  : ""}
                )
              </label>
            ) : null}

            {preview?.blockers
              ?.filter((row) => row.code !== "QUESTIONS_REQUIRED")
              .map((row) => (
                <p key={row.code} className="al-cb-break-error">
                  {row.message}
                </p>
              ))}

            {preview?.missingQuestions.length ? (
              <p className="al-cb-break-error">
                Include all questions on: {preview.missingQuestions.map((row) => row.title).join(", ")}.
              </p>
            ) : null}

            {copies > 0 ? (
              <p className="al-cb-muted">
                {copies} source link{copies === 1 ? "" : "s"} on other courses will be cleared. Those copies stay.
              </p>
            ) : null}

            {preview?.libraryAttaches ? (
              <p className="al-cb-muted">
                {countLabel(preview.libraryAttaches, "library attach", "library attaches")} on these rows will be
                removed.
              </p>
            ) : null}

            <label className="al-cb-check al-cb-break-opt">
              <input
                type="checkbox"
                checked={understood}
                disabled={busy}
                onChange={(event) => setUnderstood(event.target.checked)}
              />
              I understand this cannot be undone.
            </label>
          </div>
        </div>
      )}
    </Dialog>
  );
}
